import { type FC } from "react";
import { FormProvider, type UseFormReturn, useController } from "react-hook-form";
import { twMerge } from "tailwind-merge";

import { CODE_LANG_OPTIONS } from "../../../shared/worksheets/code-kernels";
import { LabeledCheckbox } from "../LabeledCheckbox";
import { LateSubmissionIntervalDropdown } from "../LateSubmissionIntervalDropdown";

import type { GeneralFormValues } from "./types";

import { LANGUAGE_LABELS, SupportedLanguage, t } from "i18n/i18n";
import { DropdownMenu } from "mds/components/DropdownMenu";
import { Tooltip } from "mds/components/Tooltip";
import { TooltipTrigger } from "mds/components/TooltipTrigger";
import { CircleInfoIcon } from "mds/icons";
import { useAppSelector } from "store/index";
import { selectDesignatedSection } from "store/selectors";
import { useIsArchived } from "utils/hooks/use-is-archived";
import { PROJECTOR_ZOOM_ITEMS } from "utils/presentation";
import { isLinkedSection } from "utils/publishing";

interface GeneralSettingsFormProps {
  className?: string;
  form: UseFormReturn<GeneralFormValues>;
}

export const GeneralSettingsForm: FC<GeneralSettingsFormProps> = ({ className, form }) => {
  const currentSection = useAppSelector(selectDesignatedSection);
  const { isArchived } = useIsArchived();

  const { field: daysLateField } = useController({ name: "daysLate", control: form.control });

  if (!currentSection) {
    return null;
  }

  // Archived sections are read-only, so all settings are disabled (like linked sections).
  const disabled = isLinkedSection(currentSection) || isArchived(currentSection);

  const codeBlocksDefaultLanguage = form.watch("code_blocks_default_language");
  const codeBlocksEnabled = form.watch("code_blocks_enabled");
  const projectorZoom = form.watch("projector_zoom");
  const sectionLanguage = form.watch("section_language");
  const formAllowLateSubmissions = form.watch("allowLateSubmissions");

  const unspecifiedLanguageId = "unspecified";
  const languageOptions = Object.entries(LANGUAGE_LABELS)
    .map(([id, label]) => ({
      id,
      title: label,
    }))
    .concat([{ id: unspecifiedLanguageId, title: t("settings.section_language_unspecified") }]);

  return (
    <FormProvider {...form}>
      <div
        className={twMerge(
          "flex flex-col text-sm font-medium leading-[1.3125rem] text-black-tint-10",
          className,
        )}
      >
        <div className="flex flex-col gap-2">
          <label className="flex items-center justify-between">
            <span>{t("settings.section.section_language")}</span>
            <input type="hidden" {...form.register("section_language")} />
            <div className="inline-block">
              <DropdownMenu
                disabled={disabled}
                eventName="Course language changed"
                eventPropertyKey="newLanguage"
                items={languageOptions}
                selectedValue={sectionLanguage || "unspecified"}
                onSelectionChange={({ id }) =>
                  form.setValue("section_language", id as SupportedLanguage, { shouldDirty: true })
                }
              />
            </div>
          </label>
          <span className="text-xs text-black-tint-55">
            {t("settings.section_language_tooltip")}
          </span>

          <LabeledCheckbox
            eventName="Section Settings - Student AI chat toggle"
            registration={form.register("student_ai_assistant_enabled", { disabled })}
          >
            {t("settings.section.student_ai_assistant_enabled")}
          </LabeledCheckbox>

          <LabeledCheckbox
            eventName="Section Settings - Anonymous grading toggle"
            registration={form.register("anonymous_grading_enabled", { disabled })}
          >
            {t("settings.section.anonymous_grading_enabled")}
          </LabeledCheckbox>

          <Tooltip content={t("settings.hiding_static_blocks_tooltip")}>
            <LabeledCheckbox
              eventName="Section Settings - Non-interactive blocks toggle"
              registration={form.register("hide_static_blocks_on_student_response_page", {
                disabled,
              })}
            >
              {t("settings.section.hide_static_blocks_on_student_response_page")}
            </LabeledCheckbox>
          </Tooltip>

          <LabeledCheckbox
            eventName="Section Settings - Code blocks toggle"
            registration={form.register("code_blocks_enabled", { disabled })}
          >
            {t("settings.section.code_blocks_enabled")}
          </LabeledCheckbox>

          <LabeledCheckbox
            eventName="Section Settings - Lock pages at due date toggle"
            registration={form.register("should_lock_pages_at_due_date", { disabled })}
          >
            {t("settings.section.lock_pages_at_due_date")}
          </LabeledCheckbox>

          <div className="flex items-center gap-1">
            <LabeledCheckbox
              eventName="Section Settings - Allow late submissions toggle"
              registration={form.register("allowLateSubmissions", { disabled })}
            >
              {t("settings.section.allow_late_submissions")}
            </LabeledCheckbox>
            <TooltipTrigger content={t("settings.section.allow_late_submissions_tooltip")}>
              <CircleInfoIcon className="text-black-tint-40" size="s" />
            </TooltipTrigger>
          </div>

          <label className="ml-5 flex items-center gap-2">
            <span
              className={twMerge(
                formAllowLateSubmissions ? "text-black-tint-20" : "text-black-tint-55",
              )}
            >
              {t("settings.section.grace_period")}
            </span>
            <LateSubmissionIntervalDropdown
              disabled={!formAllowLateSubmissions || disabled}
              value={daysLateField.value}
              onChange={daysLateField.onChange}
            />
          </label>

          <label className="flex items-center justify-between">
            <span>{t("settings.default_code_block_language")}</span>
            <input type="hidden" {...form.register("code_blocks_default_language")} />
            <div className="inline-block">
              <DropdownMenu
                disabled={!codeBlocksEnabled || disabled}
                eventName="Section Settings - Programming language changed"
                eventPropertyKey="newLanguage"
                items={CODE_LANG_OPTIONS}
                selectedValue={codeBlocksDefaultLanguage}
                onSelectionChange={({ id }) =>
                  form.setValue("code_blocks_default_language", id, { shouldDirty: true })
                }
              />
            </div>
          </label>

          <label className="flex items-center justify-between">
            <span>{t("settings.projector_zoom")}</span>
            <input type="hidden" {...form.register("projector_zoom")} />
            <div className="inline-block">
              <DropdownMenu
                disabled={disabled}
                eventName="Section Settings - Projector zoom changed"
                eventPropertyKey="zoom"
                items={PROJECTOR_ZOOM_ITEMS}
                selectedValue={projectorZoom.toString()}
                onSelectionChange={({ id }) =>
                  form.setValue("projector_zoom", parseInt(id, 10), { shouldDirty: true })
                }
              />
            </div>
          </label>
        </div>
      </div>
    </FormProvider>
  );
};
